import { Component } from 'react'

// Fail-loud boundary. Wraps the whole app so a runtime crash in any
// page renders a readable message instead of a white screen. Kept in
// place permanently — a blank page in production is never acceptable.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, componentLine: '' }
  }

  static getDerivedStateFromError(error) {
    return { error, componentLine: '' }
  }

  componentDidCatch(error, info) {
    // componentStack looks like:
    //   \n    in OrderDetail (created by ...)
    //   \n    in Routes ...
    // First non-empty line = the component that threw.
    const stack = info?.componentStack || ''
    const firstLine = stack.split('\n').map((s) => s.trim()).find(Boolean) || ''
    this.setState({ componentLine: firstLine })
    // Log to the browser console too — makes diagnosis easier.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    const { error, componentLine } = this.state
    if (!error) return this.props.children

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F9FAFB',
          color: '#0B2038',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            margin: '0 auto',
            background: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 16,
            padding: 20,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Something went wrong
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 800, marginTop: 6 }}>
            The app hit an error.
          </h1>

          <p style={{ fontSize: 13, color: '#374151', marginTop: 12 }}>
            <b>Message:</b> {String(error?.message || error)}
          </p>
          {componentLine && (
            <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
              <b>Where:</b> {componentLine}
            </p>
          )}

          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              width: '100%',
              padding: '10px 12px',
              background: '#0B2038',
              color: 'white',
              border: 0,
              borderRadius: 10,
              fontWeight: 800,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>

          {error?.stack && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ fontSize: 11, color: '#6B7280', cursor: 'pointer' }}>
                Show technical details
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  padding: 10,
                  background: '#F3F4F6',
                  borderRadius: 8,
                  fontSize: 10,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#111827',
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
